import React from 'react';
import {
  Box,
  Typography,
  Button,
  Stack,
  Grid,
  useTheme,
  alpha,
  Chip,
  Card,
  CardMedia
} from '@mui/material';
import {
  Security as SecurityIcon,
  PersonAdd as PersonAddIcon,
  Shield as ShieldIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  VerifiedUser as VerifiedUserIcon,
  Lock as LockIcon,
  AccountTree as AccountTreeIcon,
  Groups as GroupsIcon,
  VpnKey as VpnKeyIcon,
  Https as HttpsIcon,
  Policy as PolicyIcon,
  SupervisorAccount as SupervisorAccountIcon,
  ManageAccounts as ManageAccountsIcon,
  Badge as BadgeIcon
} from '@mui/icons-material';
import Navigation from '../../Navigation';
import Footer from '../../Footer';

// Import user management images
import rolesImage from '../assests/user-mangement-page/roles.png';
import permissionsImage from '../assests/user-mangement-page/permissions.png';

const UserManagementPage = ({ onNavigateToAuth }) => {
  const theme = useTheme();

  // Hero Section Component
  const HeroSection = () => (
    <Box
      sx={{
        pt: { xs: 10, md: 8 },
        pb: { xs: 8, md: 12 },
        mt: { xs: 0, md: -10 },
        backgroundColor: '#ffffff',
        textAlign: 'center',
        minHeight: { xs: '80vh', md: '85vh' },
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Background Design Elements */}
      <Box sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 0,
        opacity: 0.1
      }}>
        <Box sx={{
          position: 'absolute',
          top: '20%',
          right: '10%',
          width: { xs: 150, md: 200 },
          height: { xs: 150, md: 200 },
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #dc2626, #ef4444)',
          animation: 'float 7s ease-in-out infinite'
        }} />
        <Box sx={{
          position: 'absolute',
          bottom: '30%',
          left: '15%',
          width: { xs: 120, md: 150 },
          height: { xs: 120, md: 150 },
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #be123c, #e11d48)',
          animation: 'float 9s ease-in-out infinite reverse'
        }} />
        <Box sx={{
          position: 'absolute',
          top: '60%',
          right: '25%',
          width: { xs: 100, md: 120 },
          height: { xs: 100, md: 120 },
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #dc2626, #ef4444)',
          animation: 'float 5s ease-in-out infinite'
        }} />
        <style>
          {`
            @keyframes float {
              0%, 100% { transform: translateY(0px) rotate(0deg); }
              50% { transform: translateY(-25px) rotate(5deg); }
            }
          `}
        </style>
      </Box>

      <Box sx={{ maxWidth: '850px', mx: 'auto', px: { xs: 2, md: 3 }, position: 'relative', zIndex: 1 }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { xs: '1.75rem', md: '4.2rem' },
            fontWeight: 400,
            color: '#2d3748',
            mb: { xs: 2.5, md: 4 },
            lineHeight: 1.1
          }}
        >
          Secure Your Workspace with{' '}
          <Box 
            component="span" 
            sx={{ 
              backgroundColor: '#dc2626',
              color: '#ffffff',
              px: { xs: 2, md: 3 },
              py: { xs: 0.75, md: 1 },
              borderRadius: { xs: 2, md: 3 },
              display: 'inline-block',
              fontWeight: 500
            }}
          >
            Advanced Access Control
          </Box>
        </Typography>
        <Typography
          variant="h5"
          sx={{
            fontSize: { xs: '1rem', md: '1.5rem' },
            fontWeight: 400,
            color: '#718096',
            mb: { xs: 3, md: 5 },
            fontStyle: 'italic',
            px: { xs: 1, md: 0 }
          }}
        >
          Complete user management with custom roles, hierarchies, and granular permission control!
        </Typography>
        <Stack direction="row" spacing={{ xs: 1.5, md: 2 }} justifyContent="center">
          <Button
            variant="contained"
            size="large"
            onClick={onNavigateToAuth}
            sx={{
              backgroundColor: '#dc2626',
              color: '#ffffff',
              fontWeight: 500,
              px: { xs: 3, md: 5 },
              py: { xs: 1.5, md: 2 },
              textTransform: 'none',
              fontSize: { xs: '0.95rem', md: '1.1rem' },
              borderRadius: { xs: 2, md: 3 },
              '&:hover': {
                backgroundColor: '#b91c1c'
              }
            }}
          >
            Start Managing Access
          </Button>
        </Stack>
        <Typography
          variant="body2"
          sx={{
            color: '#9ca3af',
            mt: { xs: 2.5, md: 4 },
            fontSize: { xs: '0.8rem', md: '0.9rem' }
          }}
        >
          Enterprise-grade security controls - Begin securing your workspace immediately
        </Typography>
      </Box>
    </Box>
  );

  // Scrolling Tags Component
  const ScrollingTags = () => (
    <Box sx={{ 
      width: '100%', 
      py: { xs: 2, md: 3 },
      mt: { xs: -8, md: -20 },
      bgcolor: alpha(theme.palette.grey[50], 0.8),
      overflow: 'hidden',
      position: 'relative'
    }}>
      <Box sx={{
        display: 'flex',
        gap: { xs: 2, md: 3 },
        animation: { xs: 'scroll 40s linear infinite', md: 'scroll 65s linear infinite' },
        width: 'max-content',
        '@keyframes scroll': {
          '0%': {
            transform: 'translateX(0)'
          },
          '100%': {
            transform: 'translateX(-50%)'
          }
        }
      }}>
        {[
          'User Management',
          'Role-Based Access',
          'Custom Permissions',
          'Hierarchy Control',
          'User Invitations',
          'Access Management',
          'Custom Roles',
          'Security Control',
          'Permission Levels',
          'Team Security',
          'Access Analytics',
          'User Directory',
          'Role Assignment',
          'Permission Matrix',
          'Security Policies',
          'Access Logs',
          'User Management',
          'Role-Based Access',
          'Custom Permissions',
          'Hierarchy Control',
          'User Invitations',
          'Access Management',
          'Custom Roles',
          'Security Control',
          'Permission Levels',
          'Team Security',
          'Access Analytics',
          'User Directory',
          'Role Assignment',
          'Permission Matrix',
          'Security Policies',
          'Access Logs'
        ].map((tag, index) => (
          <Chip
            key={index}
            label={tag}
            sx={{
              fontSize: { xs: '0.85rem', md: '1rem' },
              fontWeight: 500,
              px: { xs: 2, md: 3 },
              py: { xs: 1, md: 1.5 },
              height: 'auto',
              borderRadius: { xs: 2, md: 3 },
              bgcolor: 'white',
              color: 'text.primary',
              border: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
              boxShadow: `0 4px 12px ${alpha(theme.palette.grey[900], 0.08)}`,
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              whiteSpace: 'nowrap',
              cursor: 'default',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: `0 8px 20px ${alpha('#dc2626', 0.15)}`,
                bgcolor: alpha('#dc2626', 0.05),
                borderColor: alpha('#dc2626', 0.3),
                color: '#dc2626'
              }
            }}
          />
        ))}
      </Box>
      
      {/* Gradient overlays for smooth fade effect */}
      <Box sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: { xs: 60, md: 100 },
        height: '100%',
        background: `linear-gradient(to right, ${alpha(theme.palette.grey[50], 0.8)}, transparent)`,
        pointerEvents: 'none',
        zIndex: 1
      }} />
      <Box sx={{
        position: 'absolute',
        top: 0,
        right: 0,
        width: { xs: 60, md: 100 },
        height: '100%',
        background: `linear-gradient(to left, ${alpha(theme.palette.grey[50], 0.8)}, transparent)`,
        pointerEvents: 'none',
        zIndex: 1
      }} />
    </Box>
  );

  // Features Overview Component - Zigzag Layout
  const FeaturesOverview = () => {
    const mainFeatures = [
      {
        icon: <SecurityIcon sx={{ fontSize: 60, color: '#ffffff' }} />,
        title: 'Complete User Management',
        description: 'Comprehensive user administration with invitations, role assignments, and complete access control capabilities for your entire organization.',
        color: '#dc2626',
        items: ['User invitations', 'Role assignments', 'Access control', 'User directory']
      },
      {
        icon: <ShieldIcon sx={{ fontSize: 60, color: '#ffffff' }} />,
        title: 'Custom Roles & Hierarchy',
        description: 'Create custom roles with flexible hierarchy levels. Drag and drop to reorganize, define clear reporting structures and permission inheritance.',
        color: '#be123c',
        items: ['Custom role creation', 'Hierarchy management', 'Drag-and-drop levels', 'Role organization']
      },
      {
        icon: <VpnKeyIcon sx={{ fontSize: 60, color: '#ffffff' }} />,
        title: 'Granular Permissions',
        description: 'Fine-grained permission control with view, edit, delete, and special permissions for every feature and module in your workspace.',
        color: '#e11d48',
        items: ['View permissions', 'Edit permissions', 'Delete permissions', 'Special controls']
      }
    ];

    return (
      <Box sx={{ py: 8, mt: 20 }}>
        <Box sx={{ px: { xs: 2, md: 0 } }}>
          <Typography
            variant="h2"
            sx={{
              textAlign: 'center',
              mb: 8,
              fontSize: { xs: '1.75rem', md: '3.2rem' },
              fontWeight: 300,
              color: '#1e293b',
              maxWidth: '800px',
              mx: 'auto'
            }}
          >
            Everything You Need for{' '}
            <Box component="span" sx={{ fontWeight: 500, color: '#dc2626' }}>Security Excellence</Box>
          </Typography>
        </Box>
        
        {mainFeatures.map((feature, index) => (
          <Box 
            key={index}
            sx={{ 
              py: 6,
              backgroundColor: index % 2 === 0 ? 'transparent' : alpha('#f8fafc', 0.7)
            }}
          >
            <Box sx={{ 
              maxWidth: '1300px', 
              mx: 'auto', 
              px: { xs: 2, md: 4 },
              display: 'flex',
              flexDirection: { xs: 'column', md: index % 2 === 0 ? 'row' : 'row-reverse' },
              gap: { xs: 3, md: 6 },
              alignItems: 'center'
            }}>
              <Box sx={{ flex: 1, width: '100%' }}>
                <Box sx={{ 
                  p: { xs: 3, md: 6 },
                  backgroundColor: feature.color,
                  borderRadius: { xs: 3, md: 6 },
                  color: 'white',
                  position: 'relative',
                  overflow: 'hidden',
                  width: '100%',
                  boxShadow: `0 20px 60px ${alpha(feature.color, 0.3)}`
                }}>
                  <Box sx={{ 
                    position: 'absolute',
                    top: -20,
                    right: -20,
                    width: { xs: 80, md: 120 },
                    height: { xs: 80, md: 120 },
                    borderRadius: '50%',
                    backgroundColor: alpha('#ffffff', 0.1)
                  }} />
                  <Box sx={{ mb: { xs: 2, md: 3 } }}>
                    {feature.icon}
                  </Box>
                  <Typography variant="h4" sx={{ fontWeight: 500, mb: { xs: 2, md: 3 }, fontSize: { xs: '1.3rem', md: '1.8rem' } }}>
                    {feature.title}
                  </Typography>
                  <Typography variant="body1" sx={{ fontSize: { xs: '0.95rem', md: '1.1rem' }, lineHeight: 1.6, mb: { xs: 2.5, md: 4 }, opacity: 0.9 }}>
                    {feature.description}
                  </Typography>
                  <Stack spacing={{ xs: 0.75, md: 1 }}>
                    {feature.items.map((item, idx) => (
                      <Box key={idx} sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ 
                          width: { xs: 6, md: 8 }, 
                          height: { xs: 6, md: 8 }, 
                          borderRadius: '50%', 
                          backgroundColor: '#ffffff', 
                          mr: { xs: 1.5, md: 2 }
                        }} />
                        <Typography variant="body2" sx={{ fontSize: { xs: '0.9rem', md: '1rem' } }}>
                          {item}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              </Box>
              
              <Box sx={{ flex: 1, width: '100%' }}>
                <Box sx={{ 
                  display: 'flex',
                  flexDirection: 'column',
                  gap: { xs: 2, md: 3 }
                }}>
                  {/* Unique feature highlights for each section */}
                  {index === 0 && (
                    <>
                      <Box sx={{ 
                        p: { xs: 3, md: 4 }, 
                        backgroundColor: '#ffffff',
                        borderRadius: { xs: 3, md: 4 },
                        boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
                        border: '1px solid #e2e8f0',
                        textAlign: 'center'
                      }}>
                        <PersonAddIcon sx={{ fontSize: { xs: 32, md: 40 }, color: feature.color, mb: { xs: 1.5, md: 2 } }} />
                        <Typography variant="h6" sx={{ fontWeight: 500, mb: 1, fontSize: { xs: '0.95rem', md: '1rem' } }}>
                          User Invitations
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#64748b', fontSize: { xs: '0.85rem', md: '0.9rem' } }}>
                          Invite users via email with automatic account setup
                        </Typography>
                      </Box>
                      
                      <Box sx={{ 
                        p: { xs: 3, md: 4 }, 
                        backgroundColor: '#ffffff',
                        borderRadius: { xs: 3, md: 4 },
                        boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
                        border: '1px solid #e2e8f0',
                        textAlign: 'center'
                      }}>
                        <ManageAccountsIcon sx={{ fontSize: { xs: 32, md: 40 }, color: feature.color, mb: { xs: 1.5, md: 2 } }} />
                        <Typography variant="h6" sx={{ fontWeight: 500, mb: 1, fontSize: { xs: '0.95rem', md: '1rem' } }}>
                          Active Users
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#64748b', fontSize: { xs: '0.85rem', md: '0.9rem' } }}>
                          Manage all active users and their access levels
                        </Typography>
                      </Box>
                    </>
                  )}
                  
                  {index === 1 && (
                    <>
                      <Box sx={{ 
                        p: { xs: 3, md: 4 }, 
                        backgroundColor: '#ffffff',
                        borderRadius: { xs: 3, md: 4 },
                        boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
                        border: '1px solid #e2e8f0',
                        textAlign: 'center'
                      }}>
                        <AccountTreeIcon sx={{ fontSize: { xs: 32, md: 40 }, color: feature.color, mb: { xs: 1.5, md: 2 } }} />
                        <Typography variant="h6" sx={{ fontWeight: 500, mb: 1, fontSize: { xs: '0.95rem', md: '1rem' } }}>
                          Hierarchy Levels
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#64748b', fontSize: { xs: '0.85rem', md: '0.9rem' } }}>
                          Create unlimited hierarchy levels for your organization
                        </Typography>
                      </Box>
                      
                      <Box sx={{ 
                        p: { xs: 3, md: 4 }, 
                        backgroundColor: '#ffffff',
                        borderRadius: { xs: 3, md: 4 },
                        boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
                        border: '1px solid #e2e8f0',
                        textAlign: 'center'
                      }}>
                        <BadgeIcon sx={{ fontSize: { xs: 32, md: 40 }, color: feature.color, mb: { xs: 1.5, md: 2 } }} />
                        <Typography variant="h6" sx={{ fontWeight: 500, mb: 1, fontSize: { xs: '0.95rem', md: '1rem' } }}>
                          Custom Roles
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#64748b', fontSize: { xs: '0.85rem', md: '0.9rem' } }}>
                          Design roles that match your organization structure
                        </Typography>
                      </Box>
                    </>
                  )}
                  
                  {index === 2 && (
                    <>
                      <Box sx={{ 
                        p: { xs: 3, md: 4 }, 
                        backgroundColor: '#ffffff',
                        borderRadius: { xs: 3, md: 4 },
                        boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
                        border: '1px solid #e2e8f0',
                        textAlign: 'center'
                      }}>
                        <LockIcon sx={{ fontSize: { xs: 32, md: 40 }, color: feature.color, mb: { xs: 1.5, md: 2 } }} />
                        <Typography variant="h6" sx={{ fontWeight: 500, mb: 1, fontSize: { xs: '0.95rem', md: '1rem' } }}>
                          View/Edit/Delete
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#64748b', fontSize: { xs: '0.85rem', md: '0.9rem' } }}>
                          Control every action with granular permission settings
                        </Typography>
                      </Box>
                      
                      <Box sx={{ 
                        p: { xs: 3, md: 4 }, 
                        backgroundColor: '#ffffff',
                        borderRadius: { xs: 3, md: 4 },
                        boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
                        border: '1px solid #e2e8f0',
                        textAlign: 'center'
                      }}>
                        <HttpsIcon sx={{ fontSize: { xs: 32, md: 40 }, color: feature.color, mb: { xs: 1.5, md: 2 } }} />
                        <Typography variant="h6" sx={{ fontWeight: 500, mb: 1, fontSize: { xs: '0.95rem', md: '1rem' } }}>
                          Special Permissions
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#64748b', fontSize: { xs: '0.85rem', md: '0.9rem' } }}>
                          Define custom permissions for specific features
                        </Typography>
                      </Box>
                    </>
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
        ))}
      </Box>
    );
  };

  // Image Showcase Component - Large Image Focus
  const ImageShowcase = () => (
    <Box sx={{ py: { xs: 6, md: 12 }, mt: { xs: -6, md: -10 } }}>
      {/* First Image Section - Roles & Hierarchy */}
      <Box sx={{ mb: { xs: 8, md: 16 } }}>
        {/* Header Section */}
        <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 8 }, px: { xs: 2, md: 4 } }}>
          <Stack direction="row" justifyContent="center" sx={{ mb: { xs: 2, md: 3 } }}>
            <Chip 
              icon={<AccountTreeIcon />}
              label="Roles & Hierarchy"
              variant="outlined"
              sx={{ 
                borderColor: '#dc2626',
                color: '#dc2626',
                fontWeight: 500,
                px: { xs: 2, md: 3 },
                py: { xs: 0.75, md: 1 },
                fontSize: { xs: '0.9rem', md: '1rem' },
                '& .MuiChip-icon': {
                  color: '#dc2626'
                }
              }}
            />
          </Stack>
          
          <Typography variant="h2" sx={{ 
            fontWeight: 500,
            color: '#1e293b',
            mb: { xs: 2.5, md: 4 },
            fontSize: { xs: '1.75rem', md: '3.5rem' },
            maxWidth: '800px',
            mx: 'auto',
            px: { xs: 1, md: 0 }
          }}>
            Custom{' '}
            <Box component="span" sx={{ color: '#dc2626' }}>Roles & Hierarchy</Box>
          </Typography>
          
          <Typography variant="h6" sx={{ 
            color: '#64748b',
            fontSize: { xs: '1rem', md: '1.3rem' },
            maxWidth: '600px',
            mx: 'auto',
            lineHeight: 1.6,
            px: { xs: 2, md: 0 }
          }}>
            Create and organize custom roles with flexible hierarchy levels and drag-and-drop management
          </Typography>
        </Box>

        {/* Large Image Display */}
        <Box sx={{ 
          maxWidth: '1400px',
          mx: 'auto',
          px: { xs: 2, md: 4 },
          mb: { xs: 4, md: 8 }
        }}>
          <Box sx={{
            position: 'relative',
            borderRadius: 2,
            overflow: 'hidden'
          }}>
            <img 
              src={rolesImage}
              alt="Roles and Hierarchy Management"
              style={{ 
                width: '100%', 
                maxWidth: '1400px',
                height: 'auto',
                borderRadius: '8px',
                display: 'block'
              }}
            />
          </Box>
        </Box>

        {/* Feature Grid Below Image */}
        <Box sx={{ 
          maxWidth: '1200px', 
          mx: 'auto', 
          px: { xs: 2, md: 4 },
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: { xs: 3, md: 4 },
          alignItems: 'stretch'
        }}>
          {[
            { 
              icon: AccountTreeIcon, 
              title: 'Hierarchy Management',
              desc: 'Create unlimited hierarchy levels and organize roles with drag-and-drop simplicity'
            },
            { 
              icon: BadgeIcon, 
              title: 'Custom Roles',
              desc: 'Design custom roles with unique names, descriptions, and permission sets'
            },
            { 
              icon: GroupsIcon, 
              title: 'Visual Organization',
              desc: 'See your entire role structure at a glance with clear visual indicators'
            }
          ].map((item, idx) => (
            <Box key={idx} sx={{ flex: 1, width: '100%' }}>
              <Box sx={{ 
                textAlign: 'center',
                p: { xs: 3, md: 4 },
                backgroundColor: '#ffffff',
                borderRadius: { xs: 3, md: 4 },
                border: '1px solid #e2e8f0',
                boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
                height: '100%',
                '&:hover': {
                  boxShadow: '0 8px 40px rgba(220, 38, 38, 0.12)',
                  transform: 'translateY(-4px)',
                  transition: 'all 0.3s ease'
                }
              }}>
                <Box sx={{
                  width: { xs: 56, md: 64 },
                  height: { xs: 56, md: 64 },
                  borderRadius: '50%',
                  backgroundColor: alpha('#dc2626', 0.1),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: { xs: 2, md: 3 }
                }}>
                  <item.icon sx={{ fontSize: { xs: 28, md: 32 }, color: '#dc2626' }} />
                </Box>
                <Typography variant="h6" sx={{ 
                  fontWeight: 500,
                  color: '#1e293b',
                  mb: { xs: 1.5, md: 2 },
                  fontSize: { xs: '1.05rem', md: '1.2rem' }
                }}>
                  {item.title}
                </Typography>
                <Typography variant="body2" sx={{ 
                  color: '#64748b',
                  lineHeight: 1.6,
                  fontSize: { xs: '0.95rem', md: '1rem' }
                }}>
                  {item.desc}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Permissions Section */}
      <Box sx={{ backgroundColor: alpha('#f8fafc', 0.6), py: { xs: 6, md: 12 }, mt: { xs: -8, md: -15 } }}>
        {/* Header Section */}
        <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 8 }, px: { xs: 2, md: 4 } }}>
          <Stack direction="row" justifyContent="center" sx={{ mb: { xs: 2, md: 3 } }}>
            <Chip 
              icon={<VpnKeyIcon />}
              label="Granular Permissions"
              variant="outlined"
              sx={{ 
                borderColor: '#be123c',
                color: '#be123c',
                fontWeight: 500,
                px: { xs: 2, md: 3 },
                py: { xs: 0.75, md: 1 },
                fontSize: { xs: '0.9rem', md: '1rem' },
                '& .MuiChip-icon': {
                  color: '#be123c'
                }
              }}
            />
          </Stack>
          
          <Typography variant="h2" sx={{ 
            fontWeight: 500,
            color: '#1e293b',
            mb: { xs: 2.5, md: 4 },
            fontSize: { xs: '1.75rem', md: '3.5rem' },
            maxWidth: '800px',
            mx: 'auto',
            px: { xs: 1, md: 0 }
          }}>
            Detailed{' '}
            <Box component="span" sx={{ color: '#be123c' }}>Permission Control</Box>
          </Typography>
          
          <Typography variant="h6" sx={{ 
            color: '#64748b',
            fontSize: { xs: '1rem', md: '1.3rem' },
            maxWidth: '600px',
            mx: 'auto',
            lineHeight: 1.6,
            px: { xs: 2, md: 0 }
          }}>
            Fine-grained access control with view, edit, delete, and special permissions for every feature
          </Typography>
        </Box>

        {/* Permissions Image Display */}
        <Box sx={{ 
          maxWidth: '1400px',
          mx: 'auto',
          px: { xs: 2, md: 4 },
          mb: { xs: 4, md: 8 }
        }}>
          <Box sx={{
            position: 'relative',
            borderRadius: 2,
            overflow: 'hidden',
          }}>
            <img 
              src={permissionsImage}
              alt="Granular Permission Control"
              style={{ 
                width: '100%', 
                maxWidth: '1400px',
                height: 'auto',
                borderRadius: '8px',
                display: 'block'
              }}
            />
          </Box>
        </Box>

        {/* Feature List Below Images */}
        <Box sx={{ 
          maxWidth: '1000px', 
          mx: 'auto', 
          px: { xs: 2, md: 4 },
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: { xs: 4, md: 6 },
          alignItems: 'stretch'
        }}>
          {[
            { 
              icon: LockIcon, 
              title: 'View, Edit, Delete',
              desc: 'Control access at every level with view, edit, and delete permissions for all features'
            },
            { 
              icon: HttpsIcon, 
              title: 'Special Permissions',
              desc: 'Define custom special permissions for advanced features and administrative functions'
            },
            { 
              icon: PolicyIcon, 
              title: 'Module-Based Control',
              desc: 'Set permissions per module with organized navigation-based permission structure'
            }
          ].map((item, idx) => (
            <Box key={idx} sx={{ flex: 1, width: '100%' }}>
              <Box sx={{ textAlign: 'center' }}>
                <Box sx={{
                  width: { xs: 64, md: 72 },
                  height: { xs: 64, md: 72 },
                  borderRadius: '50%',
                  backgroundColor: alpha('#be123c', 0.1),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: { xs: 2, md: 3 },
                  border: `2px solid ${alpha('#be123c', 0.2)}`
                }}>
                  <item.icon sx={{ fontSize: { xs: 32, md: 36 }, color: '#be123c' }} />
                </Box>
                <Typography variant="h6" sx={{ 
                  fontWeight: 500,
                  color: '#1e293b',
                  mb: { xs: 1.5, md: 2 },
                  fontSize: { xs: '1.15rem', md: '1.3rem' }
                }}>
                  {item.title}
                </Typography>
                <Typography variant="body1" sx={{ 
                  color: '#64748b',
                  lineHeight: 1.6,
                  fontSize: { xs: '1rem', md: '1.1rem' }
                }}>
                  {item.desc}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Additional Features Section */}
      <Box sx={{ py: { xs: 6, md: 12 }, mt: { xs: -8, md: -15 } }}>
        {/* Security Features Section */}
        <Box sx={{ backgroundColor: alpha('#dc2626', 0.05), py: { xs: 4, md: 8 }, borderRadius: { xs: 3, md: 4 }, mx: { xs: 2, md: 4 } }}>
          <Typography variant="h3" sx={{ 
            textAlign: 'center',
            fontWeight: 500,
            color: '#1e293b',
            mb: { xs: 4, md: 6 },
            fontSize: { xs: '1.5rem', md: '2.8rem' },
            px: { xs: 2, md: 0 }
          }}>
            Enterprise-Grade Access Control & Security
          </Typography>
          
          <Box sx={{ 
            maxWidth: '1200px', 
            mx: 'auto', 
            px: { xs: 2, md: 4 },
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: { xs: 4, md: 6 },
            alignItems: 'stretch'
          }}>
            {[
              { 
                icon: PersonAddIcon, 
                title: 'User Invitations & Onboarding',
                desc: 'Invite users via email with automatic account setup. Manage pending invitations and streamline the onboarding process.',
                color: '#dc2626'
              },
              { 
                icon: ShieldIcon, 
                title: 'Custom Role Hierarchy',
                desc: 'Build unlimited hierarchy levels with drag-and-drop organization. Create roles that perfectly match your organizational structure.',
                color: '#be123c'
              },
              { 
                icon: VpnKeyIcon, 
                title: 'Granular Permission Matrix',
                desc: 'Control every action with view, edit, delete, and special permissions across all modules. Set permissions at feature level for complete control.',
                color: '#e11d48'
              }
            ].map((item, idx) => (
              <Box key={idx} sx={{ flex: 1, width: '100%' }}>
                <Box sx={{ 
                  textAlign: 'center',
                  p: { xs: 4, md: 6 },
                  backgroundColor: '#ffffff',
                  borderRadius: { xs: 3, md: 4 },
                  border: '1px solid #e2e8f0',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
                  height: '100%',
                  '&:hover': {
                    boxShadow: `0 12px 40px ${alpha(item.color, 0.15)}`,
                    transform: 'translateY(-4px)',
                    transition: 'all 0.3s ease'
                  }
                }}>
                  <Box sx={{
                    width: { xs: 64, md: 80 },
                    height: { xs: 64, md: 80 },
                    borderRadius: '50%',
                    backgroundColor: alpha(item.color, 0.1),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: { xs: 3, md: 4 },
                    border: `3px solid ${alpha(item.color, 0.2)}`
                  }}>
                    <item.icon sx={{ fontSize: { xs: 32, md: 40 }, color: item.color }} />
                  </Box>
                  <Typography variant="h5" sx={{ 
                    fontWeight: 500,
                    color: '#1e293b',
                    mb: { xs: 2, md: 3 },
                    fontSize: { xs: '1.2rem', md: '1.4rem' }
                  }}>
                    {item.title}
                  </Typography>
                  <Typography variant="body1" sx={{ 
                    color: '#64748b',
                    lineHeight: 1.7,
                    fontSize: { xs: '1rem', md: '1.1rem' }
                  }}>
                    {item.desc}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* Floating Stats Cards */}
      <Box sx={{ position: 'relative', mt: { xs: 4, md: -8 } }}>
        <Box sx={{ 
          maxWidth: '1000px', 
          mt: 10,
          mx: 'auto', 
          px: { xs: 2, md: 4 },
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: { xs: 3, md: 4 },
          alignItems: 'stretch'
        }}>
          {[
            { icon: SecurityIcon, number: '100%', label: 'Secure Access Control', color: '#dc2626' },
            { icon: VerifiedUserIcon, number: 'Custom', label: 'Role Hierarchies', color: '#be123c' },
            { icon: VpnKeyIcon, number: '500+', label: 'Permission Settings', color: '#e11d48' }
          ].map((stat, idx) => (
            <Box key={idx} sx={{ flex: 1, width: '100%' }}>
              <Card sx={{ 
                p: { xs: 3, md: 4 },
                textAlign: 'center',
                backgroundColor: '#ffffff',
                boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                borderRadius: { xs: 3, md: 4 },
                border: '1px solid #e2e8f0',
                transform: { xs: 'translateY(0)', md: 'translateY(-20px)' },
                '&:hover': {
                  transform: { xs: 'translateY(0)', md: 'translateY(-25px)' },
                  boxShadow: '0 25px 50px rgba(0,0,0,0.15)',
                  transition: 'all 0.3s ease'
                }
              }}>
                <Box sx={{ 
                  width: { xs: 64, md: 80 },
                  height: { xs: 64, md: 80 },
                  borderRadius: '50%',
                  backgroundColor: alpha(stat.color, 0.1),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: { xs: 2, md: 3 }
                }}>
                  <stat.icon sx={{ fontSize: { xs: 32, md: 40 }, color: stat.color }} />
                </Box>
                <Typography variant="h2" sx={{ 
                  fontWeight: 500,
                  color: stat.color,
                  mb: 1,
                  fontSize: { xs: '2.5rem', md: '3rem' }
                }}>
                  {stat.number}
                </Typography>
                <Typography variant="h6" sx={{ 
                  fontWeight: 500,
                  color: '#374151',
                  fontSize: { xs: '1.05rem', md: '1.2rem' }
                }}>
                  {stat.label}
                </Typography>
              </Card>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );

  // CTA Section Component
  const CTASection = () => (
    <Box
      sx={{
        py: { xs: 6, md: 8 },
        backgroundColor: '#dc2626',
        color: '#fff',
        textAlign: 'center'
      }}
    >
      <Box sx={{ maxWidth: '600px', mx: 'auto', px: { xs: 2, md: 3 } }}>
        <Typography
          variant="h3"
          sx={{
            fontSize: { xs: '1.8rem', md: '2.2rem' },
            fontWeight: 400,
            mb: { xs: 2, md: 3 },
            color: '#ffffff'
          }}
        >
          Ready to secure your workspace?
        </Typography>
        <Typography
          variant="h6"
          sx={{
            fontSize: { xs: '1rem', md: '1.1rem' },
            fontWeight: 400,
            mb: { xs: 3, md: 4 },
            color: '#fecaca'
          }}
        >
          Join organizations that have implemented enterprise-grade access control with custom roles, hierarchies, and granular permissions.
        </Typography>
        <Button
          variant="contained"
          size="large"
          onClick={onNavigateToAuth}
          sx={{
            backgroundColor: '#ffffff',
            color: '#dc2626',
            fontWeight: 500,
            px: { xs: 4, md: 5 },
            py: { xs: 1.5, md: 2 },
            textTransform: 'none',
            fontSize: { xs: '1rem', md: '1.1rem' },
            borderRadius: 2,
            border: '2px solid #dc2626',
            '&:hover': {
              backgroundColor: '#dc2626',
              color: '#ffffff'
            }
          }}
        >
          Start Managing Access
        </Button>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ minHeight: '100vh' }}>
      <Navigation onNavigateToAuth={onNavigateToAuth} />
      <HeroSection />
      <ScrollingTags />
      <FeaturesOverview />
      <ImageShowcase />
      <CTASection />
      <Footer />
    </Box>
  );
};

export default UserManagementPage;